/**
 * The technician's safety screens (FM Technician R3, slice S9: B34 permits as
 * receiver, B35 lock register, B36 tools & instruments) and the FM's tools and
 * locks pages (C27): the response shapes, the query keys and the one refresh
 * every safety write does.
 *
 * Shapes: backend/routers/tech_safety.py (and the extended GET /fm/permits/{id}).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { noSignal } from '@/components/Tech/offlineJobs';
import { apiGet } from '@/utils/api';
import { invalidateJobs } from './techApi';
import type { Tone } from './techParts';

export type TechPermitType = 'hot_work' | 'work_at_height' | 'confined_space' | 'electrical_isolation' | 'cold_work';
export type TechPermitState = 'requested' | 'active' | 'expired' | 'closed' | 'cancelled';
export type ExceptionState = 'none' | 'asked' | 'approved' | 'refused';
export type SpaceKind = 'sewer' | 'septic' | 'stp_sump' | 'tank' | 'other';

export interface SiteRef { id: string; name: string; timezone: string | null }
export interface JobRef { id: string; ref: string; title: string; site_id: string }

export interface TechPermitRow {
  id: string; number: string; type: TechPermitType; type_label: string;
  site_id: string; site: string | null; location: string; work: string;
  state: TechPermitState; valid_from: string | null; valid_to: string | null; timezone: string;
  receiver: string | null; receiver_is_me: boolean; requested_by_me: boolean;
  needs_my_signature: boolean; receiver_signed_at: string | null;
  work_order_id: string | null; work_order_ref: string | null;
  exception_state: ExceptionState;
}

export interface PermitTypeNeeds {
  fire_watch: boolean; standbys: boolean; loto: boolean; gas_test: boolean;
  fire_zone: boolean; space: boolean; voltage: boolean;
}
export interface TechPermitTypeInfo { type: TechPermitType; label: string; checklist: string[]; needs: PermitTypeNeeds }
export interface SpaceKindInfo { value: SpaceKind; label: string; blocked: boolean }

export interface TechPermitList {
  permits: TechPermitRow[]; sites: SiteRef[]; types: TechPermitTypeInfo[];
  space_kinds: SpaceKindInfo[]; jobs: JobRef[];
}

export type Calibration = 'ok' | 'due_soon' | 'expired' | 'none';

export interface Tester {
  id: string; name: string; serial: string | null; calibration_due: string | null;
  calibration: Calibration; calibration_words: string; with_me: boolean; usable: boolean; why: string | null;
}

export interface LockRow {
  id: string; lock_no: string; tag_no: string | null; isolation_point: string;
  building_id: string; site: string | null; timezone: string;
  holder: string; mine: boolean;
  work_order_id: string | null; work_order_ref: string | null;
  permit_id: string | null; permit_ref: string | null;
  applied_at: string; removed_at: string | null; removed_by: string | null;
  photo_url: string | null; can_remove: boolean;
}

export interface TechPermitDetail extends TechPermitRow {
  revalidations: number; issuer: string | null; receiver_id: string | null;
  receiver_signed_on: 'own_phone' | 'issuer_device' | null; receiver_signed_by: string | null;
  voltage_class: 'LT' | 'HT' | null;
  checklist: { item: string; answer: 'done' | 'not_required' | null }[];
  fire_watch_name: string | null; standbys: string[]; loto_ref: string | null;
  needs: { fire_watch: boolean; standbys: boolean; loto: boolean; gas_test: boolean;
           fire_zone: boolean; insulation_test: boolean };
  fire_zone_isolated: boolean;
  space_kind: SpaceKind | null; space_kind_label: string | null; manual_entry: boolean;
  exception: { state: ExceptionState; rescue_plan: string | null; decided_by: string | null; decided_at: string | null };
  gas_tests: { at: string; lel_pct: number; o2_pct: number; safe: boolean;
               h2s_ppm?: number | null; co_ppm?: number | null; by?: string | null }[];
  insulation_test: { value_mohm: number; tool_name: string; serial: string | null; at: string } | null;
  close_reason: string | null; closed_at: string | null; closed_by: string | null;
  my_locks: LockRow[];
  testers: Tester[];
  can: { sign: boolean; sign_why: string | null; close_out: boolean; close_out_why: string | null };
}

export interface LockList { locks: LockRow[]; sites: SiteRef[];
  permits: { id: string; number: string; type_label: string; site_id: string }[]; jobs: JobRef[] }

export type ToolKind = 'insulation_tester' | 'multimeter' | 'clamp_meter' | 'gas_detector' | 'other';

export interface ToolRow {
  id: string; name: string; kind: ToolKind; kind_label: string; serial: string | null;
  building_id: string; site: string | null; timezone: string;
  calibration_due: string | null; calibration: Calibration; calibration_words: string;
  with: 'me' | 'store' | 'someone'; holder: string | null; issued_at: string | null;
  can_take: { allowed: boolean; why: string | null }; can_return: boolean;
}
export interface ToolList { tools: ToolRow[]; sites: SiteRef[] }

export interface FmToolRow extends ToolRow { active: boolean; issue_count: number; can_delete: boolean }
export interface FmToolList { tools: FmToolRow[]; sites: SiteRef[]; kinds: { value: ToolKind; label: string }[] }
export interface FmLockList { locks: LockRow[]; sites: SiteRef[] }

/** Calibration as a word + a tone (the Pill draws the icon). */
export const CALIBRATION: Record<Calibration, [Tone, string]> = {
  ok: ['green', 'Valid'],
  due_soon: ['amber', 'Due soon'],
  expired: ['red', 'Expired'],
  none: ['grey', 'No date'],
};

export const SAFETY_KEYS = {
  permits: ['tech', 'permits'] as const,
  permit: (id: string) => ['tech', 'permit', id] as const,
  locks: (site: string) => ['tech', 'locks', site] as const,
  tools: (site: string) => ['tech', 'tools', site] as const,
};

const qs = (site: string) => (site ? `?site=${encodeURIComponent(site)}` : '');

export function useTechPermits() {
  return useQuery<TechPermitList>({ queryKey: SAFETY_KEYS.permits, queryFn: () => apiGet('/tech/permits'),
    refetchInterval: 60_000,
    // NO SIGNAL (D8; as useTechJobs, R2 e2e F3): React Query would PAUSE this
    // read offline instead of failing it, and the page's "No signal — data
    // from HH:MM" fallback waits for a failed read. Always send it, and do not
    // retry a read that got no answer at all.
    networkMode: 'always',
    retry: (n, e) => !noSignal(e) && n < 2 });
}
export function useTechLocks(site: string) {
  return useQuery<LockList>({ queryKey: SAFETY_KEYS.locks(site), queryFn: () => apiGet(`/tech/locks${qs(site)}`) });
}
export function useTechTools(site: string) {
  return useQuery<ToolList>({ queryKey: SAFETY_KEYS.tools(site), queryFn: () => apiGet(`/tech/tools${qs(site)}`) });
}

/** Every safety write re-reads every screen showing the row: permits (list
 *  and page), locks, tools, the home tiles, the handover draft, the job's
 *  safety check (a signed permit or a removed lock moves its steps) and the
 *  job itself (list, page, home, running bar). */
export function useTechSafetyInvalidate() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['permits', 'permit', 'locks', 'tools', 'home', 'handover', 'safety-check']) {
      void qc.invalidateQueries({ queryKey: ['tech', k] });
    }
    void qc.invalidateQueries({ queryKey: ['fm', 'tools'] });
    void qc.invalidateQueries({ queryKey: ['fm', 'locks'] });
    invalidateJobs(qc);
  };
}

/** The phone's time, sent with every write the offline queue may replay. */
export const phoneNow = () => new Date().toISOString();
